import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useBanner } from './BannerContext';

export default function BannerBar() {
  const { banner, hideBanner } = useBanner();
  const location = useLocation();

  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [offsetTop, setOffsetTop] = useState(0);
  const tickRef = useRef(null);
  const startRef = useRef(Date.now());

  // Detecta navbar fixa
  useEffect(() => {
    const calcOffset = () => {
      const nav =
        document.querySelector('.navbar.fixed-top') || document.querySelector('.fixed-top');
      const h = nav ? nav.getBoundingClientRect().height : 0;
      setOffsetTop(h);
    };
    calcOffset();
    window.addEventListener('resize', calcOffset);
    return () => window.removeEventListener('resize', calcOffset);
  }, []);

  // Montagem/desmontagem animada
  useEffect(() => {
    if (banner) {
      startRef.current = Date.now();
      setMounted(true);
      requestAnimationFrame(() => setVisible(true));
    } else {
      setVisible(false);
      const t = setTimeout(() => setMounted(false), 350);
      return () => clearTimeout(t);
    }
  }, [banner]);

  // Countdown
  const { totalMs, remainingMs } = useMemo(() => {
    if (!banner) return { totalMs: 0, remainingMs: 0 };
    if (banner.sticky) return { totalMs: 0, remainingMs: 0 };

    const start = startRef.current;
    let total = 0;
    let remain = 0;

    if (banner.expiresAt) {
      const end = new Date(banner.expiresAt).getTime();
      total = Math.max(end - start, 0);
      remain = Math.max(end - now, 0);
    } else if (typeof banner.durationMs === 'number') {
      total = Math.max(banner.durationMs, 0);
      const end = start + total;
      remain = Math.max(end - now, 0);
    }

    return { totalMs: total, remainingMs: remain };
  }, [banner, now]);

  // Timer
  useEffect(() => {
    if (!banner || banner.sticky) return;
    if (!banner.expiresAt && typeof banner.durationMs !== 'number') return;
    tickRef.current = setInterval(() => setNow(Date.now()), 100);
    return () => clearInterval(tickRef.current);
  }, [banner]);

  // Fecha automaticamente
  useEffect(() => {
    if (!banner || banner.sticky) return;
    if (totalMs > 0 && remainingMs <= 0) hideBanner(banner.id);
  }, [banner, totalMs, remainingMs, hideBanner]);

  // Não renderiza em rotas admin
  const path = location.pathname.toLowerCase();
  if (path.includes('/painel') || path.includes('/admin')) return null;
  if (!mounted || !banner) return null;

  // ==============================
  // 🔹 Estilo dinâmico por nível
  // ==============================
  const styleMap = {
    info: {
      bg: 'rgba(180, 220, 255, 0.85)',
      border: '#a5cfff',
      color: '#003366',
      icon: 'bi-info-circle-fill',
      btn: '#007bff',
      shadow: 'rgba(0, 123, 255, 0.2)',
    },
    warning: {
      bg: 'rgba(255, 230, 180, 0.85)',
      border: '#ffe0a5',
      color: '#7a4a00',
      icon: 'bi-exclamation-triangle-fill',
      btn: '#ff8c00',
      shadow: 'rgba(255, 165, 0, 0.25)',
    },
    error: {
      bg: 'rgba(255, 200, 200, 0.85)',
      border: '#ffb3b3',
      color: '#6b0000',
      icon: 'bi-x-octagon-fill',
      btn: '#d32f2f',
      shadow: 'rgba(211,47,47,0.25)',
    },
    success: {
      bg: 'rgba(200, 255, 200, 0.85)',
      border: '#a0f0a0',
      color: '#084c18',
      icon: 'bi-check-circle-fill',
      btn: '#198754',
      shadow: 'rgba(25,135,84,0.25)',
    },
    critical: {
      bg: 'rgba(255, 160, 160, 0.9)',
      border: '#ff9999',
      color: '#4b0000',
      icon: 'bi-shield-exclamation',
      btn: '#8b0000',
      shadow: 'rgba(139,0,0,0.3)',
    },
  };

  const theme = styleMap[banner.level] || styleMap.info;
  const progress =
    banner.sticky || totalMs === 0
      ? 0
      : Math.max(0, Math.min(100, Math.round(((totalMs - remainingMs) / totalMs) * 100)));

  return (
    <div
      className="position-fixed w-100"
      style={{
        top: offsetTop + 10,
        left: 0,
        zIndex: 2000,
        display: 'flex',
        justifyContent: 'center',
        transition: 'all .4s ease',
        transform: visible ? 'translateY(0)' : 'translateY(-120%)',
        opacity: visible ? 1 : 0,
      }}
    >
      <div
        className="shadow-lg d-flex align-items-center gap-3 px-4 py-3"
        style={{
          width: 'min(90%, 800px)',
          borderRadius: 12,
          background: theme.bg,
          border: `1.5px solid ${theme.border}`,
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          color: theme.color,
          boxShadow: `0 6px 24px ${theme.shadow}`,
        }}
      >
        {/* Ícone */}
        <i
          className={`bi ${theme.icon} fs-3`}
          style={{
            color: theme.btn,
            flexShrink: 0,
          }}
        />

        {/* Texto */}
        <div className="flex-grow-1">
          <strong style={{ fontSize: 16 }}>{banner.title || 'Aviso importante'}</strong>
          <div style={{ fontSize: 14, lineHeight: 1.4 }}>{banner.message}</div>
        </div>

        {/* Botão fechar */}
        <button
          className="btn btn-sm"
          style={{
            background: theme.btn,
            color: 'white',
            borderRadius: 50,
            fontWeight: 'bold',
            width: 32,
            height: 32,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
          onClick={() => hideBanner(banner.id)}
        >
          ×
        </button>
      </div>
    </div>
  );
}
